import { stringifyEntityRef } from "@backstage/catalog-model";
import { EmptyState, InfoCard, Progress } from "@backstage/core-components";
import { useApi } from "@backstage/core-plugin-api";
import { useEntity } from "@backstage/plugin-catalog-react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Link as MuiLink,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import LaunchIcon from "@material-ui/icons/Launch";
import TrendingDownIcon from "@material-ui/icons/TrendingDown";
import TrendingFlatIcon from "@material-ui/icons/TrendingFlat";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { explorerApiRef } from "../../api/ExplorerApi";
import type {
  AssignmentCostEntry,
  CostByService,
  CostHistoryEntry,
  CostSummary,
  EntityAssignmentCostsResponse,
} from "../../api/types";
import { AssignmentBadges } from "../shared/AssignmentBadges";

const useStyles = makeStyles((theme) => ({
  intro: {
    marginBottom: theme.spacing(3),
  },
  introBody: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  helperCopy: {
    color: theme.palette.text.secondary,
    maxWidth: 820,
  },
  summaryCard: {
    height: "100%",
    border: `1px solid ${theme.palette.divider}`,
  },
  metricLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  metricValue: {
    marginTop: theme.spacing(0.5),
    fontWeight: 700,
  },
  metricHint: {
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  trend: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  trendUp: {
    color: theme.palette.error.main,
  },
  trendDown: {
    color: theme.palette.success.main,
  },
  trendFlat: {
    color: theme.palette.text.secondary,
  },
  section: {
    marginTop: theme.spacing(3),
  },
  tableWrap: {
    overflowX: "auto",
  },
  historyRow: {
    display: "grid",
    gridTemplateColumns: "92px minmax(120px, 1fr) 110px",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 0),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
      gap: theme.spacing(0.5),
    },
  },
  historyTrack: {
    height: 8,
    borderRadius: 999,
    backgroundColor: theme.palette.action.hover,
    overflow: "hidden",
  },
  historyBar: {
    height: "100%",
    borderRadius: 999,
    background: `linear-gradient(90deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
  },
  accountCard: {
    border: `1px solid ${theme.palette.divider}`,
  },
  accountHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  accountTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  cloudChip: {
    fontWeight: 700,
    letterSpacing: "0.04em",
  },
  mono: {
    fontFamily: "monospace",
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    overflowWrap: "anywhere",
  },
  accountMetrics: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  accountMetric: {
    minWidth: 140,
  },
  inlineAlert: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    color: theme.palette.error.dark,
    backgroundColor: theme.palette.error.light,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
  },
  unsupported: {
    color: theme.palette.warning.dark,
    backgroundColor: theme.palette.warning.light,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(2),
  },
  subdued: {
    color: theme.palette.text.secondary,
  },
}));

function formatCurrency(value: number, currency: string): string {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatTrend(value: number): string {
  if (value === 0) return "0%";
  return `${value > 0 ? "+" : ""}${value.toFixed(1)}%`;
}

function trendClass(classes: ReturnType<typeof useStyles>, value: number) {
  if (value > 0) return classes.trendUp;
  if (value < 0) return classes.trendDown;
  return classes.trendFlat;
}

function TrendValue({ value }: { value: number }) {
  const classes = useStyles();
  const Icon =
    value > 0
      ? TrendingUpIcon
      : value < 0
        ? TrendingDownIcon
        : TrendingFlatIcon;
  return (
    <span className={`${classes.trend} ${trendClass(classes, value)}`}>
      <Icon fontSize="small" />
      {formatTrend(value)}
    </span>
  );
}

function statusLabel(status: AssignmentCostEntry["status"]): string {
  if (status === "ok") return "Incluida";
  if (status === "unsupported") return "No soportada";
  return "Error";
}

function statusColor(
  status: AssignmentCostEntry["status"],
): "primary" | "secondary" | "default" {
  if (status === "ok") return "primary";
  if (status === "error") return "secondary";
  return "default";
}

function unsupportedMessage(entry: AssignmentCostEntry): string {
  if (
    entry.assignment.cloud === "gcp" ||
    entry.message === "gcp_project_cost_requires_perspective_group_id"
  ) {
    return "GCP requiere mapear project → CloudHealth perspective group ref_id.";
  }
  return entry.message ?? "Esta cuenta todavía no está soportada para costes.";
}

function SummaryMetricCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
}) {
  const classes = useStyles();
  return (
    <Card elevation={0} className={classes.summaryCard}>
      <CardContent>
        <Typography className={classes.metricLabel}>{label}</Typography>
        <Typography variant="h5" className={classes.metricValue}>
          {value}
        </Typography>
        {hint && (
          <Typography variant="body2" className={classes.metricHint}>
            {hint}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function HistoryList({
  history,
  currency,
}: {
  history: CostHistoryEntry[];
  currency: string;
}) {
  const classes = useStyles();
  const maxCost = Math.max(...history.map((entry) => entry.cost), 0);

  if (history.length === 0) {
    return (
      <Typography variant="body2" className={classes.subdued}>
        Sin histórico disponible todavía.
      </Typography>
    );
  }

  return (
    <Box aria-label="Histórico mensual de costes">
      {history.map((entry) => {
        const width =
          maxCost > 0 ? Math.max((entry.cost / maxCost) * 100, 4) : 0;
        return (
          <Box key={entry.month} className={classes.historyRow}>
            <Typography variant="body2">{entry.month}</Typography>
            <Box className={classes.historyTrack} aria-hidden="true">
              <Box
                className={classes.historyBar}
                style={{ width: `${width}%` }}
              />
            </Box>
            <Typography variant="body2" align="right">
              {formatCurrency(entry.cost, currency)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function ServiceBreakdown({
  services,
  currency,
}: {
  services: CostByService[];
  currency: string;
}) {
  const classes = useStyles();

  if (services.length === 0) {
    return (
      <Typography variant="body2" className={classes.subdued}>
        Sin desglose por servicio para las cuentas soportadas.
      </Typography>
    );
  }

  return (
    <Box className={classes.tableWrap}>
      <Table size="small" aria-label="Desglose de costes por servicio">
        <TableHead>
          <TableRow>
            <TableCell>Servicio</TableCell>
            <TableCell align="right">Último mes cerrado</TableCell>
            <TableCell align="right">Mes cerrado anterior</TableCell>
            <TableCell align="right">Tendencia</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {services.map((service) => (
            <TableRow key={service.service} hover>
              <TableCell>{service.service}</TableCell>
              <TableCell align="right">
                {formatCurrency(service.currentMonth, currency)}
              </TableCell>
              <TableCell align="right">
                {formatCurrency(service.previousMonth, currency)}
              </TableCell>
              <TableCell align="right">
                <TrendValue value={service.trend} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function AccountCostCard({ entry }: { entry: AssignmentCostEntry }) {
  const classes = useStyles();
  const { assignment } = entry;
  const displayName = assignment.nodeDisplayName ?? assignment.accountId;

  return (
    <Card elevation={0} className={classes.accountCard}>
      <CardContent>
        <Box className={classes.accountHeader}>
          <Box>
            <Box className={classes.accountTitle}>
              <AccountBalanceWalletIcon color="primary" />
              <Typography variant="h6">{displayName}</Typography>
              <Chip
                size="small"
                color="primary"
                className={classes.cloudChip}
                label={assignment.cloud.toUpperCase()}
              />
              <Chip
                size="small"
                color={statusColor(entry.status)}
                label={statusLabel(entry.status)}
              />
              <AssignmentBadges assignment={assignment} />
            </Box>
            <Typography className={classes.mono}>
              accountId: {assignment.accountId}
            </Typography>
            <Typography className={classes.mono}>
              nodeRef: {assignment.nodeRef}
            </Typography>
          </Box>
        </Box>

        {entry.status === "ok" && entry.cost && (
          <Box className={classes.accountMetrics}>
            <Box className={classes.accountMetric}>
              <Typography className={classes.metricLabel}>
                Último mes cerrado
              </Typography>
              <Typography variant="h6">
                {formatCurrency(entry.cost.currentMonth, entry.cost.currency)}
              </Typography>
            </Box>
            <Box className={classes.accountMetric}>
              <Typography className={classes.metricLabel}>
                Mes cerrado anterior
              </Typography>
              <Typography variant="h6">
                {formatCurrency(entry.cost.previousMonth, entry.cost.currency)}
              </Typography>
            </Box>
            <Box className={classes.accountMetric}>
              <Typography className={classes.metricLabel}>Tendencia</Typography>
              <Typography variant="h6">
                <TrendValue value={entry.cost.trend} />
              </Typography>
            </Box>
          </Box>
        )}

        {entry.status === "unsupported" && (
          <Typography className={classes.unsupported} variant="body2">
            {unsupportedMessage(entry)}
          </Typography>
        )}

        {entry.status === "error" && (
          <Box role="alert" className={classes.inlineAlert} mt={2}>
            <ErrorOutlineIcon fontSize="small" />
            <Typography variant="body2">
              {entry.message ??
                "No se pudieron cargar los costes de esta cuenta."}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function SummarySection({
  response,
}: {
  response: EntityAssignmentCostsResponse;
}) {
  const summary: CostSummary = response.summary;
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={3}>
        <SummaryMetricCard
          label="Último mes cerrado"
          value={formatCurrency(summary.currentMonth, summary.currency)}
          hint="Solo cuentas soportadas"
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <SummaryMetricCard
          label="Mes cerrado anterior"
          value={formatCurrency(summary.previousMonth, summary.currency)}
          hint="Base de comparación"
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <SummaryMetricCard
          label="Tendencia"
          value={<TrendValue value={summary.trend} />}
          hint="Variación vs mes anterior"
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <SummaryMetricCard
          label="Cuentas soportadas"
          value={`${response.supportedCount}/${response.accounts.length}`}
          hint={`${response.unsupportedCount} pendientes · ${response.errors} errores`}
        />
      </Grid>
    </Grid>
  );
}

export function EntityCloudAssignmentFinOpsContent() {
  const classes = useStyles();
  const { entity } = useEntity();
  const api = useApi(explorerApiRef);
  const entityRef = useMemo(() => stringifyEntityRef(entity), [entity]);
  const [response, setResponse] = useState<EntityAssignmentCostsResponse>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);

    api
      .getEntityCosts(entityRef)
      .then((nextResponse) => {
        if (!cancelled) {
          setResponse(nextResponse);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : "No se pudieron cargar los costes por asignación directa.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [api, entityRef]);

  if (loading) {
    return <Progress />;
  }

  if (error) {
    return (
      <Box role="alert" className={classes.inlineAlert}>
        <ErrorOutlineIcon fontSize="small" />
        <Typography variant="body2">{error}</Typography>
      </Box>
    );
  }

  if (!response || response.accounts.length === 0) {
    return (
      <EmptyState
        missing="content"
        title="Sin cuentas cloud asignadas"
        description="Cuentas asignadas a esta entidad desde Cloud Explorer o catalog-info.yaml (cloudhealth.io/asset-refs). Asigna cuentas para ver costes por account en esta entidad."
        action={
          <Button
            component={RouterLink}
            to="/cloud-explorer"
            color="primary"
            variant="contained"
            endIcon={<LaunchIcon />}
          >
            Asigna cuentas desde Cloud Explorer
          </Button>
        }
      />
    );
  }

  return (
    <Box>
      <InfoCard
        title="FinOps por asignaciones de Cloud Explorer"
        className={classes.intro}
      >
        <Box className={classes.introBody}>
          <Typography variant="body2" className={classes.helperCopy}>
            Costes por asignaciones directas (Cloud Explorer + catalog-info.yaml
            con cloudhealth.io/asset-refs). AWS/Azure incluidos; GCP pendiente
            de mapping project → CloudHealth perspective group ref_id. Los
            totales excluyen cuentas no soportadas o con error.
          </Typography>
          <MuiLink component={RouterLink} to="/cloud-explorer">
            Gestionar asignaciones
          </MuiLink>
        </Box>
      </InfoCard>

      <SummarySection response={response} />

      <Grid container spacing={3} className={classes.section}>
        <Grid item xs={12} md={5}>
          <InfoCard title="Histórico mensual">
            <HistoryList
              history={response.summary.history}
              currency={response.summary.currency}
            />
          </InfoCard>
        </Grid>
        <Grid item xs={12} md={7}>
          <InfoCard title="Desglose por servicio">
            <ServiceBreakdown
              services={response.summary.byService}
              currency={response.summary.currency}
            />
          </InfoCard>
        </Grid>
      </Grid>

      <Box className={classes.section}>
        <Typography variant="h5" gutterBottom>
          Cuentas asignadas
        </Typography>
        <Grid container spacing={2}>
          {response.accounts.map((entry) => (
            <Grid item xs={12} key={entry.assignment.id}>
              <AccountCostCard entry={entry} />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}
